import { FormEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import { LoadingButton } from 'ui/loading-button'
import { toast } from 'ui/sonner'
import { Textarea } from 'ui/text-area'
import { z } from 'zod/mini'

import { apiClient } from '~/api/client'
import { LinkButton } from '~/components/link-button'
import { MobileBottomLayout } from '~/layout/mobile-bottom'
import { useAccountStore } from '~/stores/account'

export const ContactUsPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const accountUsername = useAccountStore(state => state.username)
  const accountEmail = useAccountStore(state => state.email)
  const { t } = useTranslation()

  const onSubmit: FormEventHandler = e => {
    e.preventDefault()
    const form = e.currentTarget as HTMLFormElement
    const { name, email, subject, message } = z
      .object({
        name: z.optional(z.string()),
        email: z.optional(z.email()),
        subject: z.string(),
        message: z.string(),
      })
      .parse(Object.fromEntries(new FormData(form)))

    setIsLoading(true)
    const username = name || accountUsername
    const userEmail = email || accountEmail
    if (!username || !userEmail) return toast.error(t('Please fill in all fields'))

    void apiClient
      .POST('/contact', {
        body: {
          username,
          email: userEmail,
          subject,
          message,
        },
      })
      .then(({ response }) => {
        if (response.status < 400) return setIsSuccess(true)

        toast.error(t('Something went wrong'))
      })
      .finally(() => setIsLoading(false))
  }

  if (isSuccess)
    return (
      <MobileBottomLayout>
        <h2 className="text-center text-2xl font-bold">{t('Thank you for reaching out')}</h2>
        <p className="text-gray-500 dark:text-gray-400">{t('Your message has been received and we')}</p>
        <LinkButton to="/settings" variant="outline">
          {t('Back to settings')}
        </LinkButton>
      </MobileBottomLayout>
    )

  return (
    <MobileBottomLayout>
      <h2 className="text-center text-2xl font-bold">{t('Contact Form')}</h2>
      <p className="text-gray-500 dark:text-gray-400">
        {t('Reach out to us by completing the form below We ll respond promptly to assist you')}
      </p>
      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <div className="space-y-1">
          <Label htmlFor="subject">{t('Subject')}</Label>
          <Input id="subject" name="subject" placeholder={t('Enter the subject')} required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="message">{t('Message')}</Label>
          <Textarea
            className="min-h-[100px]"
            id="message"
            name="message"
            placeholder={t('Enter your message')}
            required
          />
        </div>
        <LoadingButton className="w-full" isLoading={isLoading} type="submit">
          {t('Submit Your Message')}
        </LoadingButton>
      </form>
      <LinkButton to="/settings" variant="outline">
        {t('Back')}
      </LinkButton>
    </MobileBottomLayout>
  )
}
